// @ts-strict-ignore
import {Component, PanelLifecycleContext} from 'panel';

import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {Subscribable} from 'common/types/context/subscribable';
import {Direction} from 'common/components/resizable-panel/types';

import {ReportLayout} from './types';

const DEFAULT_USER_SIDEBAR_WIDTH = 400;

export class ReportLayoutManager implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private _isUserSidebarOpen = false;
  private _qbAlignment: Direction = null;
  private _isQbCollapsed = false;
  private _rightPanelWidth = DEFAULT_USER_SIDEBAR_WIDTH;
  private _reportLayout: ReportLayout = ReportLayout.SplitChartTable;
  private _scrollContainerListeners: Set<(ev: Event) => void> = new Set();
  private _scrollContainer: HTMLElement = null;
  private _whiteBackground = false;
  private subscriptions = [];

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  /**
   * If config enabled, set the background to white. Allows to have in central location
   * rather than stray overrides
   */
  updateWhiteBackBackground(whiteBackground: boolean) {
    this._whiteBackground = whiteBackground;
    this.updateComponents();
  }

  getWhiteBackBackground() {
    return this._whiteBackground;
  }

  /**
   * The element that is the scroll container for the page (BannerLayout) calls this after it's rendered so that its
   * `scroll` event can be listened to.
   */
  updateScrollContainer(scrollContainer: HTMLElement) {
    const prevScrollContainer = this._scrollContainer;
    this._scrollContainer = scrollContainer;
    for (const listener of this._scrollContainerListeners) {
      prevScrollContainer?.removeEventListener(`scroll`, listener);
      scrollContainer?.addEventListener(`scroll`, listener);
    }

    this.updateComponents();
  }

  /**
   * Subscribe to `scroll` events from the page's scroll container. Example usage is progressive load in Event
   * report's table. When scrolled to bottom of page, it renders more rows.
   */
  subscribeScrollContainer(listener: (ev: Event) => void) {
    this._scrollContainerListeners.add(listener);
    this._scrollContainer?.addEventListener(`scroll`, listener);
  }

  /**
   * Usually called when subscribing component is unmounted.
   */
  unsubscribeScrollContainer(listener: (ev: Event) => void) {
    this._scrollContainerListeners.delete(listener);
    this._scrollContainer?.removeEventListener(`scroll`, listener);
  }

  getScrollContainer() {
    return this._scrollContainer;
  }

  resetReportLayoutIfNeeded({hasDataTable}: {hasDataTable: boolean}) {
    const newReportLayout = hasDataTable ? ReportLayout.SplitChartTable : ReportLayout.NonSplit;
    if (newReportLayout === this._reportLayout) {
      return;
    }
    this.updateReportLayout(newReportLayout);
  }

  updateReportLayout(reportLayout: ReportLayout) {
    this._reportLayout = reportLayout;
    this.updateComponents();
  }

  getReportLayout() {
    return this._reportLayout;
  }

  updateIsUserSidebarOpen(isUserSidebarOpen: boolean) {
    this._isUserSidebarOpen = isUserSidebarOpen;
    this.updateComponents();
  }

  getIsUserSidebarOpen() {
    return this._isUserSidebarOpen;
  }

  updateIsQbCollapsed(isQbCollapsed: boolean) {
    this._isQbCollapsed = isQbCollapsed;
    this.updateComponents();
  }

  getIsQBCollapsed() {
    return this._isQbCollapsed;
  }

  updateQbAlignment(qbAlignment: Direction) {
    this._qbAlignment = qbAlignment;
    this.updateComponents();
  }

  getQbAlignment() {
    return this._qbAlignment;
  }

  updateRightPanelWidth(rightPanelWidth: number) {
    this._rightPanelWidth = rightPanelWidth;
    this.updateComponents();
  }

  getRightPanelWidth() {
    return this._rightPanelWidth;
  }

  getUserSidebarWidth() {
    return this._qbAlignment === Direction.Right ? this._rightPanelWidth : DEFAULT_USER_SIDEBAR_WIDTH;
  }

  getTrackingProps() {
    return {
      [commonTrackingEventPropertyName.reportLayout]: this._reportLayout,
      [commonTrackingEventPropertyName.qbCollapsed]: this._isQbCollapsed,
      [commonTrackingEventPropertyName.qbAlignment]: this._qbAlignment,
    };
  }

  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
